/**
 * 隐私政策页面
 * Privacy Policy Page - 符合 GDPR, CCPA, PDPO, PIPL 要求
 */

import React from 'react';
import { useTranslation } from '../hooks/useTranslation';

interface PrivacyPolicyPageProps {
    currentLanguage: string;
    onClose: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ currentLanguage, onClose }) => {
    const { t } = useTranslation(currentLanguage);
    const isChineseLanguage = currentLanguage.startsWith('zh');

    const content = isChineseLanguage ? {
        title: '隐私政策',
        lastUpdated: '最后更新：2026年1月',
        sections: [
            {
                title: '1. 信息收集概要',
                content: `我们 ("LawHelper") 重视您的隐私。本政策描述了我们在您使用 LawHelper 服务（"服务"）时如何收集、使用和保护您的信息。

**核心收集点**：
• **您提供的信息**：注册详情（姓名、邮箱）、支付信息（由 Stripe 安全处理）、用户内容（文档、查询）。
• **自动收集的信息**：设备信息、IP 地址、浏览器类型、使用活动（时间戳、点击流）。
• **敏感信息**：我们不处理种族、宗教、健康状况等敏感个人信息。`
            },
            {
                title: '2. 我们如何使用您的信息',
                content: `我们处理您的信息的合法依据包括履行合同、法律义务和合法商业利益：

• **服务交付**：管理账户、验证身份、处理支付。
• **服务改进**：分析使用趋势、优化 AI 模型性能（不使用您的机密数据进行通用训练）。
• **沟通**：发送管理通知、安全警报和（如果您同意）营销信息。
• **安全与合规**：检测欺诈、保护服务安全、响应法律要求。`
            },
            {
                title: '3. 信息共享与披露',
                content: `我们不会出售您的个人信息。我们可能在以下情况共享信息：

• **服务提供商**：仅与必要的第三方（如 AWS 托管、Stripe 支付、邮件发送服务）共享，且受严格保密协议约束。
• **法律要求**：如果传票、法院命令或适用法律要求披露。
• **业务转让**：在合并、收购或资产出售期间可能会转移信息。
• **您的同意**：在您明确授权的情况下共享。`
            },
            {
                title: '4. 数据安全措施',
                content: `我们实施行业标准的安全措施：

• **加密**：传输中 (TLS) 和静态数据 (AES-256) 加密。
• **访问控制**：严格的员工数据访问权限管理。
• **告诫**：尽管我们尽力保护，但没有任何互联网传输是 100% 安全的。请妥善保管您的登录凭证。`
            },
            {
                title: '5. 您的隐私权利',
                content: `根据您所在的地区（如 GDPR/CCPA/PIPL），您可能享有：

• **访问与更正**：查看或修改您的个人信息。
• **删除权**：请求删除您的账户及相关数据。
• **撤回同意**：随时取消订阅营销信息或撤回特定数据处理同意。
• **不追踪 (Do-Not-Track)**：目前我们不响应浏览器的 DNT 信号。`
            },
            {
                title: '6. Cookie 与追踪技术',
                content: `我们使用 Cookie 来维护会话状态、分析流量和个性化体验。
您可以随时通过浏览器设置拒绝 Cookie，但这可能会影响服务的某些功能。`
            },
            {
                title: '7. 数据保留政策',
                content: `**保留期限**：我们仅在实现收集目的所需的时间内保留您的信息。
• 账户活跃期间：持续保留账户基本信息。
• 账户注销后：我们将在合理时间内删除或匿名化您的数据，除非法律要求保留（如税务记录）。`
            },
            {
                title: '8. 未成年人保护',
                content: `我们不向 18 岁以下未成年人提供服务。通过使用服务，您声明您已年满 18 岁。如果我们发现误收集了未成年人信息，将立即删除。`
            },
            {
                title: '9. 跨境数据传输',
                content: `本服务由位于迪拜的 LawHelper 运营，但数据服务器可能位于美国。如果您访问本服务，即表示您同意将数据跨境传输到美国并在那里进行处理。此传输对于履行我们与您的服务协议是必要的。`
            },
            {
                title: '10. 隐私政策更新',
                content: `我们可能会不时更新本政策。重大变更将通过邮件或网站通知。继续使用服务即表示您接受更新后的政策。`
            },
            {
                title: '11. 联系我们与数据请求',
                content: `如需行使您的权利或有隐私疑问，请联系：

LawHelper Inc.
隐私团队: privacy@lawhelper.ai
地址: Dubai, United Arab Emirates

如需查看、更新或删除数据，请直接发送邮件至上述地址。`
            }
        ]
    } : {
        title: 'Privacy Policy',
        lastUpdated: 'Last Updated: January 2026',
        sections: [
            {
                title: '1. Information Collection Summary',
                content: `We ("LawHelper") value your privacy. This policy describes how we collect, use, and protect your information when using our Services.

**Key Data Points**:
• **Information You Provide**: Registration details (name, email), payment data (processed securely by Stripe), and User Content (documents, queries).
• **Automated Collection**: Device info, IP address, browser type, and usage data (timestamps, clickstreams).
• **Sensitive Info**: We do NOT process sensitive personal information such as race, religion, or health status.`
            },
            {
                title: '2. How We Process Your Information',
                content: `We process your information based on legal grounds including contract performance, legal obligations, and legitimate business interests:

• **Service Delivery**: Managing accounts, authentication, processing payments.
• **Improvement**: Analyzing usage trends to optimize AI performance (without using your confidential data for general training).
• **Communication**: Sending administrative notices, security alerts, and (with consent) marketing.
• **Security & Compliance**: Fraud detection, securing system integrity, responding to legal requests.`
            },
            {
                title: '3. Sharing Your Information',
                content: `We do NOT sell your personal information. We may share data in these contexts:

• **Service Providers**: With essential third parties (e.g., AWS hosting, Stripe payments, email services) under strict confidentiality agreements.
• **Legal Obligations**: If required by a subpoena, court order, or applicable law.
• **Business Transfers**: In connection with a merger, sale of assets, or acquisition.
• **Consent**: When you explicitly authorize us to share.`
            },
            {
                title: '4. Data Security',
                content: `We implement industry-standard security measures:

• **Encryption**: TLS for data in transit and AES-256 for data at rest.
• **Access Control**: Strict employee access management.
• **Disclaimer**: While we strive for security, no internet transmission is 100% secure. Please keep your credentials safe.`
            },
            {
                title: '5. Your Privacy Rights',
                content: `Depending on your location (e.g., GDPR/CCPA/PIPL), you may have rights to:

• **Access & Rectify**: Review or update your personal information.
• **Erasure**: Request deletion of your account and data.
• **Withdraw Consent**: Opt-out of marketing or revoke specific processing consents.
• **Do-Not-Track**: We currently do not respond to DNT browser signals.`
            },
            {
                title: '6. Cookies and Tracking',
                content: `We use cookies to maintain session state, analyze traffic, and personalize your experience.
You can refuse cookies via browser settings, though this may affect some Service functionality.`
            },
            {
                title: '7. Data Retention',
                content: `**Retention Period**: We keep your information only as long as necessary.
• Active Accounts: Account data is retained while active.
• Termination: Upon deletion request, we will delete or anonymize your data within a reasonable timeframe, unless legal retention (e.g., tax logs) is required.`
            },
            {
                title: '8. Minors',
                content: `We do not knowingly assist or collect data from individuals under 18. By using the Services, you represent you are at least 18. If we verify accidental collection from a minor, we will delete it immediately.`
            },
            {
                title: '9. International Transfers',
                content: `LawHelper is based in Dubai, UAE, while our servers may be located in the United States. By using the Services, you consent to the transfer and processing of your data in the US, which is necessary for performing our contract with you.`
            },
            {
                title: '10. Updates to this Policy',
                content: `We may update this policy periodically. Material changes will be notified via email or site notice. Continued use implies acceptance of the updated policy.`
            },
            {
                title: '11. Contact Us & Data Requests',
                content: `To exercise your rights or for privacy inquiries, contact:

LawHelper Inc.
Privacy Team: privacy@lawhelper.ai
Address: Dubai, United Arab Emirates

To request review, update, or deletion of your data, please email us directly.`
            }
        ]
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation Header */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5]">
                <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        className="flex items-center gap-2 text-[#666666] hover:text-[#1A1A1A] transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="text-sm">{isChineseLanguage ? '返回' : 'Back'}</span>
                    </button>
                    <div>
                        <h1 className="text-lg font-semibold text-[#1A1A1A]">{content.title}</h1>
                    </div>
                    <div className="w-16" /> {/* Spacer for centering */}
                </div>
            </nav>

            {/* Content */}
            <div className="pt-24 pb-16 px-6">
                <div className="max-w-3xl mx-auto">
                    <p className="text-sm text-[#999999] mb-8">{content.lastUpdated}</p>
                    <div className="prose prose-lg max-w-none">
                        {content.sections.map((section, index) => (
                            <div key={index} className="mb-10">
                                <h2 className="text-xl font-semibold text-[#1A1A1A] mb-4">{section.title}</h2>
                                <div className="text-[#666666] whitespace-pre-line leading-relaxed">
                                    {section.content}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Back Button */}
                    <div className="mt-12 pt-8 border-t border-[#E5E5E5]">
                        <button
                            onClick={onClose}
                            className="px-6 py-3 bg-[#1A1A1A] text-white font-medium hover:bg-[#333333] transition-colors"
                        >
                            {isChineseLanguage ? '返回首页' : 'Back to Home'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyPage;
